import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, ShieldCheck, ShieldAlert, Flame, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ConfirmModal from "@/components/ConfirmModal";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function InboxDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dns, setDns] = useState(null);
  const [warmup, setWarmup] = useState(null);
  const [checkingDns, setCheckingDns] = useState(false);
  // Strike 8C — state-driven Disconnect confirmation (replaces window.confirm).
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const reloadAll = async () => {
    const r = await api.get(`/inboxes/${id}`);
    setData(r.data);
    api.get(`/inboxes/${id}/warmup`).then((w) => setWarmup(w.data)).catch(() => {});
  };

  useEffect(() => {
    reloadAll().catch(() => toast.error("Failed to load inbox"));
  }, [id]);

  const checkDns = async () => {
    setCheckingDns(true);
    try {
      const r = await api.get(`/inboxes/${id}/dns`);
      setDns(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DNS check failed");
    } finally {
      setCheckingDns(false);
    }
  };

  if (!data) return <div className="text-neutral-500 text-sm font-mono">Loading…</div>;

  // Defensive defaults - older or programmatic inboxes might miss nested fields.
  if (!data.sending_window) {
    data.sending_window = { start_hour: 9, end_hour: 17, days: [0, 1, 2, 3, 4] };
  }

  const update = (patch) => setData({ ...data, ...patch });
  const updateWindow = (patch) =>
    setData({ ...data, sending_window: { ...data.sending_window, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/inboxes/${id}`, {
        daily_limit: data.daily_limit,
        warmup_enabled: data.warmup_enabled,
        signature: data.signature,
        reply_to: data.reply_to,
        sending_window: data.sending_window,
        status: data.status,
        name: data.name,
      });
      setData(r.data);
      toast.success("Saved");
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = () => setDisconnectOpen(true);

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete(`/inboxes/${id}`);
      toast.success("Mailbox removed");
      setDisconnectOpen(false);
      navigate("/inboxes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    } finally {
      setDisconnecting(false);
    }
  };

  const toggleDay = (idx) => {
    const days = data.sending_window.days || [];
    const next = days.includes(idx)
      ? days.filter((d) => d !== idx)
      : [...days, idx].sort();
    updateWindow({ days: next });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <button
        onClick={() => navigate("/inboxes")}
        className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to inboxes
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
            {data.connection_type === "gmail_oauth"
              ? "Gmail OAuth"
              : data.connection_type === "gmail_smtp"
              ? "Gmail · App Password"
              : "SMTP"}
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            {data.email_address}
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <StatusBadge status={data.status} />
            <span className="text-neutral-500 text-xs font-mono">
              health · {data.health_score}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-inbox-button"
            className="flex items-center gap-2 bg-white text-black rounded-md px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
          >
            <Save className="w-3.5 h-3.5" strokeWidth={1.8} /> Save
          </button>
          <button
            onClick={remove}
            data-testid="delete-inbox-button"
            className="p-2 border border-[#27272a] rounded-md text-red-400 hover:bg-[#161616]"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <Section title="Sending limits">
        <Field label="Daily limit">
          <input
            type="number"
            min={1}
            max={500}
            value={data.daily_limit}
            onChange={(e) => update({ daily_limit: parseInt(e.target.value || 0) })}
            data-testid="daily-limit-input"
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono w-32"
          />
        </Field>
        <Field label="Display name">
          <input
            value={data.name || ""}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Jane from Acme"
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm w-72"
          />
        </Field>
        <Field label="Reply-to">
          <input
            value={data.reply_to || ""}
            onChange={(e) => update({ reply_to: e.target.value })}
            placeholder={data.email_address}
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono w-80"
          />
        </Field>
      </Section>

      <Section title="Sending window">
        <Field label="Hours">
          <div className="flex items-center gap-2 font-mono text-sm">
            <input
              type="number"
              min={0}
              max={23}
              value={data.sending_window.start_hour}
              onChange={(e) =>
                updateWindow({ start_hour: parseInt(e.target.value || 0) })
              }
              className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 w-20"
            />
            <span className="text-neutral-500">to</span>
            <input
              type="number"
              min={0}
              max={23}
              value={data.sending_window.end_hour}
              onChange={(e) =>
                updateWindow({ end_hour: parseInt(e.target.value || 0) })
              }
              className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 w-20"
            />
            <span className="text-neutral-500 text-xs uppercase tracking-[0.2em] ml-2">
              UTC
            </span>
          </div>
        </Field>
        <Field label="Days">
          <div className="flex items-center gap-1.5">
            {DAYS.map((d, i) => {
              const on = (data.sending_window.days || []).includes(i);
              return (
                <button
                  key={d}
                  onClick={() => toggleDay(i)}
                  data-testid={`day-${d.toLowerCase()}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
                    on
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-neutral-400 border-[#27272a] hover:bg-[#161616]"
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </Field>
      </Section>

      <Section title="Signature (HTML)">
        <textarea
          value={data.signature || ""}
          onChange={(e) => update({ signature: e.target.value })}
          rows={4}
          data-testid="signature-input"
          className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
          placeholder='<p>Best,<br/>Jane Doe<br/><a href="https://acme.com">acme.com</a></p>'
        />
      </Section>

      <Section title="Warmup">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Flame className="w-4 h-4 text-amber-400" strokeWidth={1.6} />
            <span>Auto inbox-to-inbox warmup</span>
            <span className="text-neutral-500 text-xs font-mono ml-2">
              (need 2+ warmup-enabled mailboxes)
            </span>
          </div>
          <button
            onClick={() => update({ warmup_enabled: !data.warmup_enabled })}
            data-testid="warmup-toggle"
            className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
              data.warmup_enabled ? "bg-emerald-500" : "bg-[#27272a]"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white transition-transform translate-y-0.5 ${
                data.warmup_enabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        {warmup && (
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Days active" value={warmup.days_active} />
              <Stat label="Sent today" value={warmup.sent_today} />
              <Stat label="Sent total" value={warmup.sent_total} />
            </div>
            <div className="mt-3 h-32 bg-[#0a0a0a] rounded-md border border-[#1a1a1a] p-2">
              <ResponsiveContainer>
                <AreaChart data={warmup.timeline}>
                  <CartesianGrid stroke="#1a1a1a" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => v.slice(5)}
                    stroke="#525252"
                    fontSize={10}
                  />
                  <YAxis stroke="#525252" fontSize={10} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0a0a",
                      border: "1px solid #1f1f1f",
                      fontFamily: "JetBrains Mono",
                      fontSize: 11,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sent"
                    stroke="#f59e0b"
                    fill="rgba(245,158,11,0.18)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Section>

      <Section title="Domain authentication">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-neutral-400 max-w-md">
            Check SPF, DKIM, and DMARC for{" "}
            <span className="font-mono text-white">
              {data.email_address.split("@")[1]}
            </span>
            . Critical for inbox placement.
          </p>
          <button
            onClick={checkDns}
            disabled={checkingDns}
            data-testid="dns-check-button"
            className="flex items-center gap-2 px-3 py-2 border border-[#27272a] rounded-md text-sm hover:bg-[#161616] disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${checkingDns ? "animate-spin" : ""}`}
              strokeWidth={1.5}
            />
            {checkingDns ? "Checking…" : "Run DNS check"}
          </button>
        </div>
        {dns && (
          <div className="space-y-2 mt-4" data-testid="dns-results">
            <div className="flex items-center gap-3">
              <div className="w-1 h-12 rounded-full bg-white" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
                  Score
                </div>
                <div className="font-mono text-2xl">{dns.score}<span className="text-neutral-500 text-sm">/100</span></div>
              </div>
            </div>
            <DnsRow label="SPF" item={dns.spf} />
            <DnsRow label="DKIM" item={dns.dkim} extra={dns.dkim?.selector ? `selector: ${dns.dkim.selector}` : null} />
            <DnsRow
              label="DMARC"
              item={dns.dmarc}
              extra={dns.dmarc?.policy ? `p=${dns.dmarc.policy}` : null}
            />
          </div>
        )}
      </Section>

      <ConfirmModal
        open={disconnectOpen}
        onClose={() => (disconnecting ? null : setDisconnectOpen(false))}
        onConfirm={confirmDisconnect}
        busy={disconnecting}
        kicker={`Inbox · ${data?.email_address || ""}`}
        title="Disconnect this mailbox?"
        description={
          <>
            This stops all outbound sending and warm-up traffic for{" "}
            <span className="font-mono text-white">{data?.email_address}</span>{" "}
            immediately. Campaigns currently using this inbox will fall back to
            their other connected inboxes. The action cannot be undone — you
            will need to re-authenticate to reconnect.
          </>
        }
        confirmLabel="Disconnect"
        variant="destructive"
        testId="inbox-disconnect-confirm"
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
        {title}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
      <div className="text-sm text-neutral-400 sm:w-40">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
        {label}
      </div>
      <div className="font-mono text-xl mt-1">{value}</div>
    </div>
  );
}

function DnsRow({ label, item, extra }) {
  const status = item?.status || "missing";
  const ok = status === "ok";
  const warn = status === "warn";
  const cls = ok
    ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20"
    : warn
    ? "text-amber-400 bg-amber-400/10 border-amber-400/20"
    : "text-red-400 bg-red-400/10 border-red-400/20";
  const Icon = ok ? ShieldCheck : ShieldAlert;
  return (
    <div
      className="flex items-start gap-3 border border-[#1a1a1a] bg-[#0a0a0a] rounded-md px-4 py-3"
      data-testid={`dns-row-${label.toLowerCase()}`}
    >
      <Icon
        className={`w-4 h-4 mt-0.5 ${ok ? "text-emerald-400" : warn ? "text-amber-400" : "text-red-400"}`}
        strokeWidth={1.5}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono">{label}</span>
          <span
            className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full border font-mono ${cls}`}
          >
            {status}
          </span>
          {extra && <span className="text-xs text-neutral-500 font-mono">{extra}</span>}
        </div>
        {item?.record && (
          <div className="text-xs text-neutral-500 font-mono mt-1 truncate">
            {item.record}
          </div>
        )}
      </div>
    </div>
  );
}
